import React, { useEffect, useState, forwardRef } from "react";
import {
  Box,
  Avatar,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Grid,
  Skeleton,
  Slide,
  Divider,
  CircularProgress,
  Portal,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  getYourProfile,
  isAdmin,
  updateUser,
} from "../../services/UserService";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../theme";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Transition for the Snackbar (slides in from the right)
function SlideTransition(props) {
  return <Slide {...props} direction="left" />;
}

const UserProfile = () => {
  const [profileInfo, setProfileInfo] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editableProfile, setEditableProfile] = useState({});
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  useEffect(() => {
    fetchProfileInfo();
  }, []);

  const fetchProfileInfo = async () => {
    try {
      const response = await getYourProfile();
      setProfileInfo(response.data);
    } catch (error) {
      console.error("Error fetching profile information:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = () => {
    setEditableProfile({ ...profileInfo });
    setIsDialogOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditableProfile({ ...editableProfile, [name]: value });
  };

  const handleSave = async () => {
    setPhase("loading");
    try {
      await updateUser(profileInfo.id, editableProfile);

      // Keep loading state visible for 1.5 seconds
      setTimeout(() => {
        setProfileInfo(editableProfile);
        setPhase(null);
        setIsDialogOpen(false);

        // Show the snackbar slightly after the dialog closes
        setTimeout(() => {
          setSnackbarMessage("Profile updated successfully!");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
        }, 300);
      }, 1500);
    } catch (error) {
      console.error("Error updating profile:", error);
      setPhase(null);
      setSnackbarMessage("Failed to update profile. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <Box
      sx={{
        width: "100%",
        mt: 2,
        opacity: 0,
        animation: "slideInUp 0.5s ease-out forwards",
        "@keyframes slideInUp": {
          "0%": { opacity: 0, transform: "translateY(20px)" },
          "100%": { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      <Box
        sx={{
          backgroundColor:
            theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
          borderRadius: "12px",
          border: `1px solid ${
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.05)"
              : "rgba(0,0,0,0.1)"
          }`,
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 4px 20px rgba(0,0,0,0.2)"
              : "0 4px 20px rgba(0,0,0,0.03)",
          overflow: "hidden",
        }}
      >
        <Box p={{ xs: 3, sm: 4 }}>
          {/* Header Section */}
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "column", md: "row" }}
            alignItems={{ xs: "center", md: "center" }}
            mb={5}
            gap={3}
            textAlign={{ xs: "center", md: "left" }}
          >
            {loading ? (
              <Skeleton variant="circular" width={110} height={110} />
            ) : (
              <Box
                sx={{
                  padding: "4px",
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${colors.blueAccent[400]} 0%, ${colors.greenAccent[400]} 100%)`,
                  boxShadow: `0px 8px 24px ${
                    theme.palette.mode === "dark"
                      ? "rgba(99, 102, 241, 0.4)"
                      : "rgba(79, 70, 229, 0.3)"
                  }`,
                }}
              >
                <Avatar
                  sx={{
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? colors.primary[400]
                        : "#ffffff",
                    color: colors.gray[100],
                    width: 102,
                    height: 102,
                    fontSize: "3rem",
                    fontWeight: "bold",
                    border: `4px solid ${
                      theme.palette.mode === "dark"
                        ? colors.primary[400]
                        : "#ffffff"
                    }`,
                  }}
                >
                  {profileInfo?.fullName ? (
                    profileInfo.fullName.charAt(0).toUpperCase()
                  ) : (
                    <PersonOutlinedIcon fontSize="large" />
                  )}
                </Avatar>
              </Box>
            )}

            <Box flex="1">
              {loading ? (
                <>
                  <Skeleton variant="text" width="60%" height={50} />
                  <Skeleton
                    variant="rounded"
                    width={100}
                    height={28}
                    sx={{ mt: 1, borderRadius: "20px" }}
                  />
                </>
              ) : (
                <>
                  <Typography
                    variant="h2"
                    fontWeight="800"
                    color={colors.gray[100]}
                    sx={{ letterSpacing: "-0.5px" }}
                  >
                    {profileInfo?.fullName || "N/A"}
                  </Typography>
                  <Box mt={1.5}>
                    <Typography
                      variant="caption"
                      color={
                        profileInfo?.role === "ADMIN"
                          ? colors.blueAccent[500]
                          : colors.greenAccent[500]
                      }
                      sx={{
                        display: "inline-block",
                        backgroundColor:
                          profileInfo?.role === "ADMIN"
                            ? theme.palette.mode === "dark"
                              ? "rgba(99, 102, 241, 0.15)"
                              : "rgba(79, 70, 229, 0.1)"
                            : theme.palette.mode === "dark"
                              ? "rgba(76, 206, 172, 0.15)"
                              : "rgba(5, 150, 105, 0.1)",
                        padding: "6px 14px",
                        borderRadius: "20px",
                        fontWeight: "700",
                        letterSpacing: "1px",
                        textTransform: "uppercase",
                      }}
                    >
                      {profileInfo?.role || "N/A"}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>

            {/* Action Button */}
            {!loading && isAdmin() && (
              <Box mt={{ xs: 2, md: 0 }} ml={{ md: "auto" }}>
                <Button
                  variant="contained"
                  onClick={handleEditClick}
                  startIcon={<EditOutlinedIcon />}
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    backgroundColor: colors.blueAccent[600],
                    color: "#ffffff",
                    fontWeight: 600,
                    borderRadius: "8px",
                    padding: "8px 24px",
                    fontSize: "0.9rem",
                    textTransform: "none",
                    boxShadow:
                      theme.palette.mode === "dark"
                        ? "0 4px 10px rgba(0,0,0,0.3)"
                        : "0 4px 12px rgba(79, 70, 229, 0.2)",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      backgroundColor: colors.blueAccent[500],
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? "0 6px 14px rgba(0,0,0,0.4)"
                          : "0 6px 16px rgba(79, 70, 229, 0.3)",
                      transform: "translateY(-2px)",
                    },
                    "&:active": {
                      transform: "scale(0.95)",
                      boxShadow: "none",
                    },
                  }}
                >
                  EDIT PROFILE
                </Button>
              </Box>
            )}
          </Box>

          {/* Profile Details Grid */}
          <Grid container spacing={3}>
            {[
              {
                label: "Full Name",
                value: profileInfo?.fullName || "N/A",
                icon: <BadgeOutlinedIcon fontSize="small" />,
              },
              {
                label: "Email",
                value: profileInfo?.email || "N/A",
                icon: <EmailOutlinedIcon fontSize="small" />,
              },
              {
                label: "Location",
                value: profileInfo?.location || "N/A",
                icon: <LocationOnOutlinedIcon fontSize="small" />,
              },
              {
                label: "Username",
                value: profileInfo?.username || "N/A",
                icon: <AccountCircleOutlinedIcon fontSize="small" />,
              },
            ].map((item, index) => (
              <Grid item xs={12} sm={6} key={index} sx={{ display: "flex" }}>
                {loading ? (
                  <Skeleton
                    variant="rounded"
                    width="100%"
                    height={90}
                    sx={{ borderRadius: "12px" }}
                  />
                ) : (
                  <Box
                    width="100%"
                    display="flex"
                    flexDirection="column"
                    p="20px"
                    borderRadius="12px"
                    backgroundColor={
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.02)"
                        : colors.primary[500]
                    }
                    border={`1px solid ${
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(0,0,0,0.08)"
                    }`}
                    sx={{
                      transition: "all 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.04)"
                            : colors.primary[600],
                        boxShadow:
                          theme.palette.mode === "dark"
                            ? "none"
                            : "0 4px 12px rgba(0,0,0,0.04)",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{ color: colors.greenAccent[500], display: "flex" }}
                      >
                        {item.icon}
                      </Box>
                      <Typography
                        variant="caption"
                        color={colors.gray[400]}
                        textTransform="uppercase"
                        letterSpacing="1px"
                        fontWeight="700"
                      >
                        {item.label}
                      </Typography>
                    </Box>
                    <Typography
                      variant="h5"
                      color={colors.gray[100]}
                      fontWeight="600"
                      mt={1}
                      sx={{ wordBreak: "break-word" }}
                    >
                      {item.value}
                    </Typography>
                  </Box>
                )}
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* Edit Profile Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={(event, reason) => {
          if (
            reason === "backdropClick" ||
            reason === "escapeKeyDown" ||
            phase === "loading"
          )
            return;
          setIsDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Transition}
        keepMounted
        PaperProps={{
          sx: {
            backgroundColor:
              theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
            backgroundImage: "none",
            borderRadius: "12px",
            boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
            border: `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.05)"
                : "rgba(0,0,0,0.05)"
            }`,
          },
        }}
      >
        <DialogTitle
          textAlign="center"
          sx={{ fontSize: "1.25rem", fontWeight: "700", pt: 3, pb: 2 }}
        >
          Edit Profile
        </DialogTitle>

        <Divider
          sx={{
            borderColor:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.1)"
                : "rgba(0,0,0,0.12)",
          }}
        />

        <DialogContent sx={{ p: 3, pt: 2 }}>
          <Grid container spacing={2}>
            {["fullName", "email", "location", "username"].map((field) => (
              <Grid item xs={12} md={6} key={field}>
                <TextField
                  fullWidth
                  variant="filled"
                  label={
                    field.charAt(0).toUpperCase() +
                    field
                      .slice(1)
                      .replace(/([A-Z])/g, " $1")
                      .trim()
                  }
                  name={field}
                  value={editableProfile[field] || ""}
                  onChange={handleInputChange}
                  margin="dense"
                  InputProps={{
                    disableUnderline: true,
                    style: { fontSize: "1rem", borderRadius: "8px" },
                  }}
                  InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                  sx={{
                    "& .MuiFilledInput-root": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.05)"
                          : "rgba(0,0,0,0.04)",
                      borderRadius: "8px",
                      border: "1px solid transparent",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.08)"
                            : "rgba(0,0,0,0.06)",
                      },
                      "&.Mui-focused": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.08)"
                            : "rgba(0,0,0,0.06)",
                        borderColor: colors.blueAccent[500],
                      },
                    },
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>

        <Divider
          sx={{
            mb: 0,
            borderColor:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.1)"
                : "rgba(0,0,0,0.12)",
          }}
        />

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: { xs: 2, sm: 3 },
            gap: { xs: 2, sm: 2 },
            flexDirection: { xs: "column-reverse", sm: "row" },
            justifyContent: { xs: "center", sm: "flex-end" },
            "& > :not(style) ~ :not(style)": {
              ml: "0 !important",
            },
          }}
        >
          <Button
            onClick={() => setIsDialogOpen(false)}
            disabled={phase === "loading"}
            variant="outlined"
            sx={{
              width: { xs: "100%", sm: "auto" },
              color: colors.gray[100],
              borderColor: colors.gray[500],
              textTransform: "none",
              fontWeight: "600",
              px: { xs: 2, sm: 4 },
              py: { xs: 1.2, sm: 1.2 },
              borderRadius: "8px",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                borderColor: colors.gray[300],
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.05)"
                    : "rgba(0,0,0,0.04)",
              },
              "&:active": {
                transform: "scale(0.95)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={phase === "loading"}
            variant="contained"
            sx={{
              width: { xs: "100%", sm: "auto" },
              backgroundColor: colors.blueAccent[600],
              color: "#ffffff",
              textTransform: "none",
              fontWeight: "600",
              px: { xs: 2, sm: 4 },
              py: { xs: 1.2, sm: 1.2 },
              borderRadius: "8px",
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 4px 10px rgba(0,0,0,0.3)"
                  : "0 4px 12px rgba(79, 70, 229, 0.2)",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                backgroundColor: colors.blueAccent[500],
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0 6px 14px rgba(0,0,0,0.4)"
                    : "0 6px 16px rgba(79, 70, 229, 0.3)",
                transform: "translateY(-1px)",
              },
              "&:active": {
                transform: "scale(0.95)",
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.12)"
                    : "rgba(0,0,0,0.12)",
                color:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.3)"
                    : "rgba(0,0,0,0.3)",
                boxShadow: "none",
              },
            }}
          >
            {phase === "loading" ? (
              <>
                Saving&nbsp;
                <CircularProgress
                  size={18}
                  sx={{ color: "inherit", ml: "8px" }}
                />
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Portal>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          TransitionComponent={SlideTransition}
          sx={{ mt: 8, zIndex: 9999 }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{
              width: "100%",
              backgroundColor:
                snackbarSeverity === "success"
                  ? colors.greenAccent[700]
                  : colors.redAccent[700],
              color: "#ffffff",
              borderRadius: "8px",
              fontWeight: 600,
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0px 4px 12px rgba(0, 0, 0, 0.4)"
                  : "0px 4px 12px rgba(0, 0, 0, 0.1)",
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Portal>
    </Box>
  );
};

export default UserProfile;
